import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef } from 'react';
import styled from 'styled-components';
import { Menu, MenuButton, MenuItem, MenuSeparator } from 'ariakit/menu';
export { useMenuState as useDropdownMenuState } from 'ariakit/menu';
import { themeGet } from '@styled-system/theme-get';
import { Box } from '../Box/index.js';
import { Divider } from '../Divider/index.js';

/* ======================================= */
const StyledMenu = styled(Box).withConfig({
  displayName: "DropdownMenu__StyledMenu",
  componentId: "sc-1otdb7x-0"
})(["& > :first-of-type:not([aria-haspopup='menu']){border-top-left-radius:", ";border-top-right-radius:", ";}& > :last-child{& > [aria-haspopup='menu']{border-bottom-left-radius:", ";border-bottom-right-radius:", ";}}& > :last-child:not([aria-haspopup='menu']){border-bottom-left-radius:", ";border-bottom-right-radius:", ";}"], themeGet('radii.radiusDefault'), themeGet('radii.radiusDefault'), themeGet('radii.radiusDefault'), themeGet('radii.radiusDefault'), themeGet('radii.radiusDefault'), themeGet('radii.radiusDefault'));
const StyledMenuItem = styled(Box).withConfig({
  displayName: "DropdownMenu__StyledMenuItem",
  componentId: "sc-1otdb7x-1"
})(["&[data-active-item]{background-color:", ";}&[aria-disabled='true']{opacity:0.3;}"], themeGet('colors.itemHovered'));
/* ======================================= */
/**
 * DropdownMenu is an accessible dropdown menu component that offers a list of chocies to the user.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/Dropdown-menu#usage)
 * - [Props](https://blockparty.endpoint.com/components/Dropdown-menu#props)
 * <br />
 *
 */
const DropdownMenu = /*#__PURE__*/forwardRef((_ref, forwardedRef) => {
  let {
    children,
    dataTestId = 'dropdown-menu',
    ...props
  } = _ref;
  return /*#__PURE__*/React__default.createElement(Menu, props, menuProps => /*#__PURE__*/React__default.createElement(StyledMenu, _extends({
    ref: forwardedRef,
    backgroundColor: "white",
    borderRadius: "radiusDefault",
    boxShadow: "medium",
    "data-test-id": dataTestId,
    outline: "none",
    zIndex: "dropdown"
  }, menuProps), children));
});
const DropdownMenuItem = /*#__PURE__*/forwardRef((_ref2, forwardedRef) => {
  let {
    children,
    disabled,
    active = false,
    ...props
  } = _ref2;
  return /*#__PURE__*/React__default.createElement(Box, {
    cursor: disabled ? 'not-allowed' : 'pointer'
  }, /*#__PURE__*/React__default.createElement(StyledMenuItem, _extends({
    ref: forwardedRef,
    _active: {
      backgroundColor: 'itemSelected'
    },
    as: MenuItem
  }, active ? {
    'data-active-item': 'true'
  } : {}, {
    _hover: {
      bg: 'itemHovered'
    },
    bg: "background",
    disabled: disabled,
    fontSize: "fontSize20",
    lineHeight: "lineHeight20",
    outline: "none",
    p: "space30",
    width: "100%"
  }, props), children));
});
/**
 * DropdownMenuTrigger is used to open the DropdownMenu. You must provide a child component.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/Dropdown-menu#usage)
 * - [Props](https://blockparty.endpoint.com/components/Dropdown-menu#props)
 * <br />
 *
 */
const DropdownMenuTrigger = /*#__PURE__*/forwardRef((_ref3, forwardedRef) => {
  let {
    children,
    ...props
  } = _ref3;
  return /*#__PURE__*/React__default.createElement(MenuButton, _extends({
    ref: forwardedRef,
    cursor: "pointer"
  }, props), referenceProps => /*#__PURE__*/React__default.cloneElement(children, referenceProps));
});
const DropdownMenuSeparator = _ref4 => {
  let {
    children,
    ...props
  } = _ref4;
  return /*#__PURE__*/React__default.createElement(Divider, _extends({
    as: MenuSeparator
  }, props));
};

export { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger };
